import React from "react";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { makePutRequest } from "../../utilities/common";
import { defaultUrl } from "../../features/actions";
import { setSharedState } from "../../features/sharedStateSlices/shareState";
import CustomModal from "../CustomModal/CustomModal";
import { LinkParentForm } from "../../layouts/editForms/LinkParentForm";
import loader from "../../assets/loader.gif";
import { generateAcademicSessions } from "../../utilities/academicSession";

// Premium Icons for modern visual cues
import {
  Eye,
  Edit,
  UserPlus,
  TrendingUp,
  Archive,
  MoreVertical,
  User,
  FileText,
} from "lucide-react";
import { StudentInvoiceDrawer } from "../invoices/StudentInvoiceDrawer";


export const StudentTable = ({
  classrooms,
  studentData,
  isLoading,
  setEditForm,
  setEditModeType,
  getEmergencyContact,
  handleArchieve,
}: any) => {
  const [showModal, setShowModal] = React.useState(false);
  const [classroomId, setClassroomId] = React.useState(null);
  const [childName, setChildName] = React.useState<string>("");
  const [studentId, setStudentId] = React.useState<string | null>(null);
  const [openLinkModal, setOpenLinkModal] = React.useState<boolean>(false);
  const [academicSession, setAcademicSession] = React.useState<string | null>(
    null,
  );
  const [claId, setClaId] = React.useState<string | null>(null);
  const [openInvoiceDrawer, setOpenInvoiceDrawer] = React.useState<boolean>(false);
  const [selectedStudentForInvoice, setSelectedStudentForInvoice] = React.useState<{
    id: string;
    name: string;
  } | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handlePromote = async () => {
    await makePutRequest(`${defaultUrl}/promote/students`, {
      students: [studentId],
      source_classroom_id: claId,
      promoted_to: classroomId,
      academic_session: academicSession,
    });
    dispatch(setSharedState());
    setShowModal(false);
  };

  const getInitials = (first: string, last: string) => {
    return `${first?.charAt(0) || ""}${last?.charAt(0) || ""}`.toUpperCase();
  };

  return (
    <>
      {isLoading ? (
        <div className="flex flex-col items-center justify-center p-12 space-y-4">
          <img
            src={loader}
            alt="Loading data"
            className="w-12 h-12 object-contain animate-pulse"
          />
          <p className="text-sm font-medium text-slate-400 tracking-wide">
            Fetching student records...
          </p>
        </div>
      ) : classrooms && studentData && studentData.length > 0 ? (
        /* The max-w-5xl/6xl container clamps the width so columns don't drift miles apart on wide displays */
        <div className="w-full max-w-5xl mx-auto overflow-hidden bg-white rounded-xl border border-slate-100 shadow-sm transition-all duration-300">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left table-fixed">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                  <th className="py-3 px-4 w-[10%] text-center">Profile</th>
                  <th className="py-3 px-4 w-[35%] text-left">Full Name</th>
                  <th className="py-3 px-4 w-[25%] text-left">Date of Birth</th>
                  <th className="py-3 px-4 w-[20%] text-left">Classroom</th>
                  <th className="py-3 px-4 w-[10%] text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {studentData.map((item: any, index: any) => {
                  const fullName =
                    `${item.firstname ?? ""} ${item.othername ? item.othername : ""} ${item.surname}`.trim();

                  return (
                    <tr
                      key={index}
                      className="group hover:bg-slate-50/40 transition-colors duration-150"
                    >
                      {/* Avatar */}
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-slate-100 border border-slate-200/60 overflow-hidden text-slate-600 font-semibold text-xs transition-transform duration-200 group-hover:scale-105">
                          {item?.profile_picture &&
                          typeof item.profile_picture !== "string" ? (
                            item.profile_picture
                          ) : typeof item?.profile_picture === "string" &&
                            item.profile_picture.startsWith("http") ? (
                            <img
                              src={item.profile_picture}
                              alt={fullName}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-slate-400 tracking-wider">
                              {getInitials(item.firstname, item.surname)}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Name Details */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="font-semibold text-[13.5px] text-slate-800 tracking-tight group-hover:text-blue-900 transition-colors duration-150">
                          {fullName}
                        </div>
                        <span className="inline-block px-1.5 py-0.5 mt-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 tracking-wide">
                          ID: #{item.id?.substring(0, 6) || "N/A"}
                        </span>
                      </td>

                      {/* Date of Birth */}
                      <td className="py-3 px-4 whitespace-nowrap text-[13px] text-slate-600 font-medium tracking-wide">
                        {item.date_of_birth
                          ? new Date(item.date_of_birth).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )
                          : "—"}
                      </td>

                      {/* Classroom Badge */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100/50">
                          {item.classroom?.name ?? "Unassigned"}
                        </span>
                      </td>

                      {/* Options Button */}
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        <Menu
                          menuButton={
                            <MenuButton
                              onClick={() => setClaId(item.classroom?.id)}
                              className="inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm hover:text-slate-700 hover:bg-slate-50 hover:border-slate-300 focus:outline-none transition-all duration-150"
                            >
                              <MoreVertical size={14} />
                            </MenuButton>
                          }
                          transition
                          menuClassName="absolute right-0 mt-1 w-52 origin-top-right rounded-xl border border-slate-100 bg-white p-1 shadow-lg focus:outline-none z-30"
                        >
                          <MenuItem
                            onClick={() =>
                              navigate(`/admin/child-activities/${item.id}`)
                            }
                            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-slate-700 hover:bg-slate-50 hover:text-blue-600 cursor-pointer transition-colors"
                          >
                            <Eye size={14} className="text-slate-400" />
                            View Activities
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              setEditForm(item);
                              setEditModeType("child");
                            }}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-slate-700 hover:bg-slate-50 hover:text-blue-600 cursor-pointer transition-colors"
                          >
                            <Edit size={14} className="text-slate-400" />
                            Edit Profile
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              setEditForm(item);
                              setEditModeType("parent");
                            }}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-slate-700 hover:bg-slate-50 hover:text-blue-600 cursor-pointer transition-colors"
                          >
                            <UserPlus size={14} className="text-slate-400" />
                            Add Parent
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              setEditForm(item);
                              setEditModeType("pickup");
                              getEmergencyContact(item.id);
                            }}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-slate-700 hover:bg-slate-50 hover:text-blue-600 cursor-pointer transition-colors"
                          >
                            <User size={14} className="text-slate-400" />
                            Emergency Contact
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              setSelectedStudentForInvoice({
                                id: item.id,
                                name: `${item.surname || ""} ${item.firstname || ""}`.trim(),
                              });
                              setOpenInvoiceDrawer(true);
                            }}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-slate-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                          >
                            <FileText size={14} className="text-blue-500" />
                            Manage Invoices
                          </MenuItem>

                          <div className="my-1 border-t border-slate-100" />

                          <MenuItem
                            onClick={() => {
                              setChildName(
                                `${item?.surname || ""} ${item?.firstname || ""}`,
                              );
                              setStudentId(item?.id);
                              setShowModal(true);
                            }}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 cursor-pointer transition-colors"
                          >
                            <TrendingUp size={14} className="text-slate-400" />
                            Promote Student
                          </MenuItem>

                          <MenuItem
                            onClick={() => handleArchieve(item.id)}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-rose-600 hover:bg-rose-50 cursor-pointer transition-colors"
                          >
                            <Archive size={14} className="text-rose-400" />
                            Archive Record
                          </MenuItem>
                        </Menu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-12 text-center bg-slate-50/50 border border-dashed border-slate-200 rounded-xl max-w-5xl mx-auto">
          <p className="text-sm font-semibold text-slate-700">
            No students found
          </p>
          <p className="text-xs text-slate-400 mt-1">
            There are no records matching your current selection.
          </p>
        </div>
      )}

      {/* Promotion Modal */}
      {showModal && (
        <CustomModal
          title={`Promote Student`}
          width={36}
          hideModal={() => setShowModal(false)}
          children={
            <div className="flex flex-col gap-5 p-5 bg-white rounded-xl">
              <div className="text-center space-y-0.5">
                <p className="text-slate-400 text-[10px] uppercase tracking-wider font-bold">
                  Action Required
                </p>
                <h2 className="text-lg font-bold text-slate-800">
                  {childName}
                </h2>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Target Classroom
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {classrooms?.map((classroom: any) => {
                    const active = classroomId === classroom.id;
                    return (
                      <button
                        key={classroom.id}
                        type="button"
                        onClick={() => setClassroomId(classroom.id)}
                        className={`p-2.5 rounded-lg border text-xs font-semibold transition-all text-left ${
                          active
                            ? "bg-slate-900 border-slate-900 text-white shadow-sm"
                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {classroom.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Academic Session
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {generateAcademicSessions(2025).map(({ value: s }) => {
                    const active = academicSession === s;
                    return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setAcademicSession(s)}
                          className={`text-center py-2 rounded-lg text-xs font-bold border transition-all ${
                            active
                              ? "bg-slate-900 border-slate-900 text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          {s}
                        </button>
                      );
                    },
                  )}
                </div>
              </div>

              {classroomId && academicSession && (
                <div className="bg-blue-50/70 border border-blue-100 rounded-lg p-3.5 text-xs">
                  <p className="text-slate-600 leading-normal">
                    Promoting{" "}
                    <span className="font-semibold text-slate-900">
                      {childName}
                    </span>{" "}
                    to{" "}
                    <span className="font-bold text-blue-900">
                      {classrooms.find((c: any) => c.id === classroomId)?.name}
                    </span>{" "}
                    for session{" "}
                    <span className="font-bold text-blue-900">
                      {academicSession}
                    </span>
                    .
                  </p>
                </div>
              )}

              <div className="flex gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={handlePromote}
                  disabled={!classroomId || !academicSession}
                  className="flex-1 py-2.5 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Confirm
                </button>
              </div>
            </div>
          }
        />
      )}

      {/* Linking Forms Modal */}
      {openLinkModal && (
        <CustomModal
          title={`Link Parent Record`}
          width={45}
          hideModal={() => setOpenLinkModal(false)}
          children={
            <div className="p-0.5">
              <LinkParentForm
                student_id={studentId}
                close={() => setOpenLinkModal(false)}
              />
            </div>
          }
        />
      )}

      {/* Student Invoice History Drawer */}
      {openInvoiceDrawer && selectedStudentForInvoice && (
        <StudentInvoiceDrawer
          studentId={selectedStudentForInvoice.id}
          studentName={selectedStudentForInvoice.name}
          isOpen={openInvoiceDrawer}
          onClose={() => setOpenInvoiceDrawer(false)}
        />
      )}
    </>
  );
};
